// Weather Service for K-Skin AI
// Fetches local weather data to personalize product texture recommendations
// Currently uses mock data - can be swapped to WeatherAPI.com

export type Season = 'spring' | 'summer' | 'fall' | 'winter'
export type TextureRecommendation = 'lightweight' | 'medium' | 'rich'

export interface WeatherInsight {
  location: string // "Los Angeles, CA"
  tempRange: { min: number; max: number } // Fahrenheit
  avgHumidity: number // percentage (0-100)
  uvIndex: number // 1-11 scale
  season: Season
  skinImplications: string[]
  textureRecommendation: TextureRecommendation
}

// Season detection from month
export function detectSeason(date: Date = new Date()): Season {
  const month = date.getMonth() // 0-11
  if (month >= 2 && month <= 4) return 'spring'
  if (month >= 5 && month <= 7) return 'summer'
  if (month >= 8 && month <= 10) return 'fall'
  return 'winter'
}

// Determine texture recommendation based on weather
function getTextureRecommendation(humidity: number, avgTemp: number): TextureRecommendation {
  // High humidity or hot weather = lightweight
  if (humidity > 70 || avgTemp > 85) {
    return 'lightweight'
  }
  // Low humidity or cold weather = rich
  if (humidity < 40 || avgTemp < 50) {
    return 'rich'
  }
  // Moderate conditions = medium
  return 'medium'
}

// Generate skin implications based on weather
function getSkinImplications(
  humidity: number,
  avgTemp: number,
  uvIndex: number,
  season: Season
): string[] {
  const implications: string[] = []

  // Humidity implications
  if (humidity > 70) {
    implications.push('High humidity may increase oiliness - lighter textures recommended')
  } else if (humidity < 40) {
    implications.push('Low humidity can cause dryness - extra hydration needed')
  } else {
    implications.push('Moderate humidity - balanced hydration needs')
  }

  // Temperature implications
  if (avgTemp > 85) {
    implications.push('Hot weather increases sebum - mattifying products may help')
  } else if (avgTemp < 50) {
    implications.push('Cold weather can compromise skin barrier - richer moisturizers recommended')
  }

  // UV implications
  if (uvIndex >= 8) {
    implications.push('Very high UV exposure - SPF 50+ strongly recommended')
  } else if (uvIndex >= 6) {
    implications.push('High UV exposure - daily sunscreen essential')
  } else if (uvIndex >= 3) {
    implications.push('Moderate UV - sunscreen recommended for outdoor activities')
  }

  // Season-specific
  if (season === 'spring') {
    implications.push('Transitional season - watch for sensitivity from pollen')
  } else if (season === 'winter') {
    implications.push('Winter can weaken barrier - focus on ceramides and occlusive ingredients')
  }

  return implications.slice(0, 4) // Limit to 4 implications
}

// Mock ZIP code to location mapping (for demo)
const ZIP_TO_LOCATION: Record<string, { city: string; state: string; climate: 'humid' | 'dry' | 'moderate' }> = {
  '90210': { city: 'Beverly Hills', state: 'CA', climate: 'dry' },
  '10001': { city: 'New York', state: 'NY', climate: 'humid' },
  '33101': { city: 'Miami', state: 'FL', climate: 'humid' },
  '85001': { city: 'Phoenix', state: 'AZ', climate: 'dry' },
  '60601': { city: 'Chicago', state: 'IL', climate: 'moderate' },
  '98101': { city: 'Seattle', state: 'WA', climate: 'humid' },
  '75201': { city: 'Dallas', state: 'TX', climate: 'moderate' },
  '30301': { city: 'Atlanta', state: 'GA', climate: 'humid' },
  '02101': { city: 'Boston', state: 'MA', climate: 'humid' },
  '80201': { city: 'Denver', state: 'CO', climate: 'dry' },
}

// Generate mock weather based on location and season
function getMockWeather(
  zipCode: string,
  season: Season
): { humidity: number; tempMin: number; tempMax: number; uvIndex: number } {
  const location = ZIP_TO_LOCATION[zipCode] || { climate: 'moderate' }

  // Base values by season
  const seasonData = {
    spring: { tempMin: 55, tempMax: 72, uvIndex: 5, baseHumidity: 55 },
    summer: { tempMin: 72, tempMax: 92, uvIndex: 8, baseHumidity: 60 },
    fall: { tempMin: 48, tempMax: 68, uvIndex: 4, baseHumidity: 50 },
    winter: { tempMin: 35, tempMax: 52, uvIndex: 2, baseHumidity: 45 },
  }

  const base = seasonData[season]

  // Adjust humidity based on climate
  let humidity = base.baseHumidity
  if (location.climate === 'humid') humidity += 20
  if (location.climate === 'dry') humidity -= 15

  // Adjust temperature for dry climates (hotter in summer, colder in winter)
  let tempMin = base.tempMin
  let tempMax = base.tempMax
  if (location.climate === 'dry') {
    tempMax += 8
    tempMin -= 5
  }

  return {
    humidity: Math.min(100, Math.max(10, humidity)),
    tempMin,
    tempMax,
    uvIndex: base.uvIndex,
  }
}

/**
 * Get weather insight for a ZIP code
 * Currently uses mock data - can be replaced with real API call
 */
export async function getWeatherInsight(zipCode: string): Promise<WeatherInsight> {
  // Validate ZIP code format
  if (!/^\d{5}$/.test(zipCode)) {
    throw new Error('Invalid ZIP code format')
  }

  const season = detectSeason()
  const location = ZIP_TO_LOCATION[zipCode] || { city: 'Your Area', state: '', climate: 'moderate' as const }
  const weather = getMockWeather(zipCode, season)

  const avgTemp = (weather.tempMin + weather.tempMax) / 2
  const textureRecommendation = getTextureRecommendation(weather.humidity, avgTemp)
  const skinImplications = getSkinImplications(weather.humidity, avgTemp, weather.uvIndex, season)

  return {
    location: location.state ? `${location.city}, ${location.state}` : location.city,
    tempRange: { min: weather.tempMin, max: weather.tempMax },
    avgHumidity: weather.humidity,
    uvIndex: weather.uvIndex,
    season,
    skinImplications,
    textureRecommendation,
  }
}

/**
 * Real API implementation (for future use)
 * Uncomment and use when WeatherAPI.com key is available
 */
// export async function getWeatherInsightFromAPI(zipCode: string): Promise<WeatherInsight> {
//   const API_KEY = import.meta.env.VITE_WEATHER_API_KEY
//   const response = await fetch(
//     `https://api.weatherapi.com/v1/forecast.json?key=${API_KEY}&q=${zipCode}&days=7&aqi=no`
//   )
//
//   if (!response.ok) {
//     throw new Error('Failed to fetch weather data')
//   }
//
//   const data = await response.json()
//
//   // Extract and process weather data
//   const temps = data.forecast.forecastday.map((day: any) => ({
//     min: day.day.mintemp_f,
//     max: day.day.maxtemp_f,
//   }))
//
//   const avgHumidity = data.forecast.forecastday.reduce(
//     (sum: number, day: any) => sum + day.day.avghumidity, 0
//   ) / data.forecast.forecastday.length
//
//   const uvIndex = Math.max(...data.forecast.forecastday.map((day: any) => day.day.uv))
//
//   // ... continue processing
// }
